import React from 'react';
import { Loader2 } from 'lucide-react';
import { fullButtons } from '../../styles/colors';
import { CONTROL_SM } from '../../styles/pageLayout';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  success?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className = '', 
    variant = 'primary', 
    size = 'md', 
    loading = false, 
    success = false,
    icon, 
    children, 
    disabled,
    ...props 
  }, ref) => {
    // Standard SaaS UX:
    // - show focus ring only for keyboard (focus-visible)
    // - no ring offset to avoid "double border" look on white surfaces
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const variantClasses = {
      primary: `${fullButtons.primary} focus-visible:ring-primary-500`,
      secondary: fullButtons.secondary,
      outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-primary-500',
      ghost: 'text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-500',
      danger: `${fullButtons.danger} focus-visible:ring-red-500`,
    };
    
    const sizeClasses = {
      sm: `${CONTROL_SM} px-3 text-sm`,
      md: 'h-10 px-4 text-sm',
      lg: 'h-12 px-6 text-base',
    };
    
    const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${success ? fullButtons.success : ''} ${className}`;

    return (
      <button
        className={classes}
        ref={ref}
        disabled={disabled || loading || success}
        {...props}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : success ? (
          <span className="w-4 h-4 mr-2">✓</span>
        ) : icon ? (
          <span className={children ? 'mr-2' : ''}>{icon}</span>
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
